import { Link } from "react-router-dom";
import { CheckCircle2, Phone, Mail } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page">
      <div className="bg-[#0B0F19] text-white py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 ici-grid-bg-dark opacity-40" />
        <div className="max-w-4xl mx-auto px-4 lg:px-8 relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">About</div>
          <h1 className="mt-2 font-display text-4xl lg:text-6xl font-extrabold tracking-tight">Precision components for the vehicles you rely on</h1>
          <p className="mt-4 text-slate-400 max-w-2xl">
            InterCorp Industries is a United States-based automotive parts supplier focused on reliable products, clear
            vehicle-application information, efficient fulfillment, and dependable customer service. Our growing catalog
            includes fuel injectors and related automotive components for a wide range of domestic, European, and Asian
            vehicles.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16 space-y-10">
        <section className="rounded-2xl border border-slate-200 bg-white p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900">What we do</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            We source, verify, and list injector components with clear vehicle compatibility information, OEM and manufacturer
            part numbers, and — where available — bench-test data. Our operations focus on efficient order processing,
            responsive customer support, and dependable nationwide shipping.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {[
              "Real product data on every listing",
              "Vehicle-verified fitment information",
              "OEM, interchange, and manufacturer part numbers",
              "Test-report items clearly labeled",
              "Nationwide shipping via USPS and UPS",
              "Direct fitment help by email",
            ].map((v) => (
              <div key={v} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-[#0055FF] mt-0.5" /> {v}</div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Where our products come from</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Our catalog is drawn from our verified inventory listed on the official InterCorp Industries eBay marketplace
            store. Every product on this site links back to its corresponding eBay listing so customers can review the
            marketplace record, including feedback and buyer protection.
          </p>
          <a href="https://www.ebay.com/usr/intercorp_industries" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex text-[#0055FF] hover:underline text-sm">
            View our eBay store →
          </a>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Get in touch</h2>
          <p className="mt-3 text-slate-600">Have a fitment question or an unusual part-number request? We&apos;re here to help.</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <a data-testid="about-phone" href="tel:+16267103581" className="rounded-xl border border-slate-200 hover:border-[#0055FF] transition-colors p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center"><Phone className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Phone</div>
                <div className="text-slate-900 font-heading font-semibold">(626) 710-3581</div>
              </div>
            </a>
            <a data-testid="about-email" href="mailto:intercorpindustries1@gmail.com" className="rounded-xl border border-slate-200 hover:border-[#0055FF] transition-colors p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center"><Mail className="w-4 h-4" /></div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Email</div>
                <div className="text-slate-900 font-heading font-semibold break-all">intercorpindustries1@gmail.com</div>
              </div>
            </a>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/fitment-assistance" className="px-4 py-2 rounded-md bg-[#0055FF] text-white text-sm hover:bg-[#0044CC]">Fitment assistance</Link>
            <Link to="/contact" className="px-4 py-2 rounded-md border border-slate-200 text-slate-700 text-sm hover:bg-slate-50">Contact form</Link>
            <Link to="/wholesale" className="px-4 py-2 rounded-md border border-slate-200 text-slate-700 text-sm hover:bg-slate-50">Wholesale</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
